import { ArrowRight, Sparkles, AlertCircle, RotateCcw, Wand2 } from "lucide-react";
import DisclaimerBanner from "../DisclaimerBanner";

function SuggestedProtocolCard({ protocol, onApply, onSave }) {
  return (
    <div className="mt-6 bg-white border border-terracotta-500/30 rounded-3xl p-6" data-testid="suggested-protocol">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div>
          <div className="label-overline text-terracotta-600 mb-1">AI-suggested protocol</div>
          <div className="font-heading text-xl text-ink-900">{protocol.name}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={onApply} className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm" data-testid="apply-protocol-btn">Apply to session</button>
          <button onClick={onSave} className="min-h-[40px] px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium" data-testid="save-suggested-protocol-btn">Save to library</button>
        </div>
      </div>
      {protocol.description && <p className="text-sm text-ink-700 mb-3">{protocol.description}</p>}
      <div className="space-y-1.5">
        {(protocol.steps || []).map((s, i) => (
          <div key={`step-${s.technique || i}`} className="text-sm text-ink-700 flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-paper-100 text-xs flex items-center justify-center shrink-0">{i + 1}</span>
            <span>
              <span className="font-medium text-ink-900">{s.technique}</span>
              {s.target && <span className="text-ink-500"> · {s.target}</span>}
              {s.duration_minutes && <span className="text-ink-500"> · {s.duration_minutes}m</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalysisSection({
  analysis,
  setPhase,
  suggestProtocol,
  suggestingProtocol,
  suggestedProtocol,
  applyProtocol,
  saveSuggestedProtocol,
  hideNav = false,
}) {
  if (!analysis) return null;
  return (
    <div data-testid="analysis-phase">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-terracotta-500" />
        <div className="label-overline text-terracotta-600">AI posture analysis</div>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-5">Findings & recommendations</h1>

      <DisclaimerBanner className="mb-6" />

      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
        <div className="label-overline mb-2">Summary</div>
        <p className="text-ink-900 leading-relaxed" data-testid="analysis-summary">{analysis.summary}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-paper-200 rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-4 h-4 text-terracotta-500" />
            <div className="label-overline text-terracotta-600">Problem areas</div>
          </div>
          <div className="space-y-3">
            {analysis.problem_areas.map((p, i) => (
              <div key={`pa-${p.area || i}`} className="border-l-2 border-terracotta-500 pl-4" data-testid={`problem-area-${i}`}>
                <div className="font-medium text-ink-900">{p.area}</div>
                <div className="text-sm text-ink-700">{p.description}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-terracotta-600 mt-1">Severity: {p.severity}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-paper-200 rounded-3xl p-6">
          <div className="label-overline mb-4">Muscle imbalances</div>
          <ul className="space-y-2.5">
            {analysis.muscle_imbalances.map((m, i) => (
              <li key={`mi-${m.slice(0, 32)}-${i}`} className="text-sm text-ink-700 flex gap-2" data-testid={`imbalance-${i}`}>
                <span className="text-sage-500">·</span><span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white border border-paper-200 rounded-3xl p-6 mb-10">
        <div className="label-overline mb-4">Recommended techniques</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {analysis.recommended_techniques.map((t, i) => (
            <div key={`rt-${t.technique || i}`} className="bg-paper-50 rounded-2xl p-4 border border-paper-200" data-testid={`technique-${i}`}>
              <div className="font-medium text-ink-900">{t.technique}</div>
              <div className="text-sm text-sage-700 mt-0.5">Target: {t.target}</div>
              <div className="text-xs text-ink-500 mt-1.5">{t.reason}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        {!hideNav && (
        <button onClick={() => setPhase("capture")} className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2" data-testid="retake-btn">
          <RotateCcw className="w-4 h-4" /> Retake photos
        </button>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="suggest-protocol-btn"
            onClick={suggestProtocol}
            disabled={suggestingProtocol}
            className="min-h-[48px] px-5 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium inline-flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {suggestingProtocol ? (
              <><div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" /> Thinking…</>
            ) : (
              <><Wand2 className="w-4 h-4" /> Suggest protocol</>
            )}
          </button>
          {!hideNav && (
          <button onClick={() => setPhase("diagram")} className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2" data-testid="to-diagram-btn">
            Mark worked areas <ArrowRight className="w-4 h-4" />
          </button>
          )}
        </div>
      </div>

      {suggestedProtocol && (
        <SuggestedProtocolCard
          protocol={suggestedProtocol}
          onApply={() => applyProtocol(suggestedProtocol)}
          onSave={saveSuggestedProtocol}
        />
      )}
    </div>
  );
}
